/**
 * AdminBybitKeysBanner — PULSE-417. Admin-only strip under BybitKeyBanner:
 * lists accounts (any user, not just the selected one) whose Bybit key
 * is dead (red) or expires in < 7 days (amber). Data: the hourly probe
 * snapshot in `bybit_account_status` via GET /admin/users/bybit-keys.
 *
 * Dismissable for the current day, like BybitKeyBanner.
 */
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Clock, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useAdminBybitKeys } from '@/hooks/useAdminUsersApi'
import { cn } from '@/lib/cn'

const DISMISS_KEY = 'pulsar.admin-bybit-banner.dismissed-on'

function todayStamp(): string {
  return new Date().toISOString().slice(0, 10)
}

export function AdminBybitKeysBanner() {
  const { t } = useTranslation('app')
  const { data } = useAdminBybitKeys()
  const [dismissed, setDismissed] = useState<string | null>(null)

  useEffect(() => {
    try { setDismissed(localStorage.getItem(DISMISS_KEY)) } catch { /* private mode */ }
  }, [])

  const bad = (data ?? []).filter((k) => k.severity === 'red' || k.severity === 'yellow')
  if (bad.length === 0) return null
  if (dismissed === todayStamp()) return null

  const isRed = bad.some((k) => k.severity === 'red')
  const items = bad
    .sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'red' ? -1 : 1))
    .map((k) => k.severity === 'red'
      ? t('bybit.admin.item_expired', { label: k.label, id: k.user_id })
      : t('bybit.admin.item_expiring', {
          label: k.label, id: k.user_id,
          days: Math.max(0, Math.floor(k.days_left ?? 0)),
        }))

  const onDismiss = () => {
    const stamp = todayStamp()
    try { localStorage.setItem(DISMISS_KEY, stamp) } catch { /* private mode */ }
    setDismissed(stamp)
  }

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-5 py-2 text-sm border-b border-line',
        isRed ? 'bg-rose/15 text-rose' : 'bg-amber/15 text-amber',
      )}
      role="alert"
    >
      {isRed ? <AlertTriangle size={16} /> : <Clock size={16} />}
      <span className="flex-1">
        {t('bybit.admin.title')} {items.join(' · ')}
      </span>
      <Link
        to="/app/admin/users"
        className="underline underline-offset-2 hover:brightness-110 font-medium whitespace-nowrap"
      >
        {t('bybit.admin.open')}
      </Link>
      <button
        onClick={onDismiss}
        className="opacity-70 hover:opacity-100"
        aria-label="dismiss"
        title="dismiss"
      >
        <X size={14} />
      </button>
    </div>
  )
}
